const ACTIONS = Object.freeze([
  'confirm',
  'back',
  'attack',
  'heavy',
  'dodge',
  'interact',
  'parry',
  'companion',
  'technique',
  'ultimate',
  'bloodline',
  'pact',
  'crouch',
  'sprint',
  'pause',
  'mute',
  'forge',
  'help',
]);

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(',');

const KEY_ACTION = Object.freeze({
  Enter: 'confirm',
  NumpadEnter: 'confirm',
  Escape: 'pause',
  KeyJ: 'attack',
  KeyK: 'heavy',
  Space: 'dodge',
  Spacebar: 'dodge',
  KeyE: 'interact',
  KeyF: 'parry',
  KeyQ: 'companion',
  KeyX: 'technique',
  KeyV: 'ultimate',
  KeyC: 'crouch',
  KeyB: 'bloodline',
  KeyR: 'pact',
  ControlLeft: 'crouch',
  ControlRight: 'crouch',
  ShiftLeft: 'sprint',
  ShiftRight: 'sprint',
  KeyM: 'mute',
  KeyH: 'help',
});

const BUTTON_ACTION = Object.freeze({
  0: ['confirm', 'attack'],
  1: ['back', 'dodge'],
  2: 'attack',
  3: ['interact', 'parry'],
  4: 'dodge',
  5: 'heavy',
  6: 'crouch',
  7: 'sprint',
  8: 'mute',
  9: 'pause',
  10: 'companion',
  11: 'forge',
  16: ['bloodline'],
  17: 'pact',
});

const clamp = value => Math.max(-1, Math.min(1, Number.isFinite(value) ? value : 0));

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const deadzone = (value, threshold) => {
  const axis = clamp(number(value));
  const limit = clamp(number(threshold, 0.18));
  const magnitude = Math.abs(axis);
  if (limit >= 0.99 || magnitude <= limit) return 0;
  return Math.sign(axis) * (magnitude - limit) / (1 - limit);
};

const normalizePair = (x, y) => {
  const left = number(x);
  const right = number(y);
  const length = Math.hypot(left, right);
  return length > 1 ? { x: left / length, y: right / length } : { x: left, y: right };
};

const buttonValue = button => {
  if (typeof button === 'number') return clamp(button);
  if (typeof button === 'boolean') return button ? 1 : 0;
  if (button?.pressed === true) return 1;
  return clamp(number(button?.value ?? (button?.pressed === false ? 0 : undefined)));
};

const buttonActive = (value, previous) => {
  const amount = buttonValue(value);
  return previous ? amount > 0.25 : amount > 0.5;
};


const buttonList = buttons => {
  if (Array.isArray(buttons)) return buttons.map(buttonValue);
  if (!buttons || typeof buttons !== 'object') return [];
  return Object.keys(buttons)
    .sort((a, b) => number(a) - number(b))
    .map(key => buttonValue(buttons[key]));
};

const directionState = (up, down, left, right) => ({ up, down, left, right });

const edgeState = (values, previous = values) => ({
  pressed: directionState(values.up && !previous.up, values.down && !previous.down, values.left && !previous.left, values.right && !previous.right),
  released: directionState(!values.up && previous.up, !values.down && previous.down, !values.left && previous.left, !values.right && previous.right),
});

const actionEdges = values => ({
  pressed: { ...values },
  released: Object.fromEntries(ACTIONS.map(action => [action, !values[action]])),
});

export function createInputController(options = {}) {
  const root = options.root ?? globalThis.document;
  const view = options.window ?? root?.defaultView ?? root?.ownerDocument?.defaultView ?? globalThis.window ?? globalThis;
  const navigator = options.navigator ?? view?.navigator;
  const provider = options.getGamepads
    ?? (typeof options.gamepadProvider === 'function' ? options.gamepadProvider : navigator?.getGamepads?.bind(navigator));
  const deadzoneThreshold = Math.max(0, Math.min(0.98, number(options.deadzone, 0.18)));
  const stickThreshold = Math.max(0, Math.min(1, number(options.stickThreshold, 0.55)));
  const pointerScale = Math.max(1, number(options.pointerScale, 100));
  const manual = Object.fromEntries(ACTIONS.map(action => [action, { down: false, pressed: false }]));
  const consumed = Object.fromEntries(ACTIONS.map(action => [action, false]));
  const keys = new Set();
  const pointer = {
    active: false,
    captured: false,
    deltaX: 0,
    deltaY: 0,
    dragging: false,
    id: null,
    type: '',
  };
  // An analogue thumbstick has no button and no key: it reports a continuous vector, so it needs
  // its own channel rather than a row in the key set. The touch pads write here, and the movement
  // and look consumers read it through the same move/look pair every other source already uses, so
  // the keyboard path is untouched and a pad still wins when both are live.
  const analog = { moveX: 0, moveY: 0, lookX: 0, lookY: 0 };
  let virtualGamepad = null;
  let previousDpad = directionState(false, false, false, false);
  let previousStick = directionState(false, false, false, false);
  let previousActions = Object.fromEntries(ACTIONS.map(action => [action, false]));
  let previousKeyboardActions = { ...previousActions };
  let previousVirtualActions = { ...previousActions };
  let previousManualActions = { ...previousActions };
  let previousSource = null;
  const buttonLatches = new Array(32).fill(false);
  let current = {
    analog: { move: { x: 0, y: 0 }, look: { x: 0, y: 0 } },
    actions: { ...previousActions },
    actionPressed: { ...previousActions },
    actionReleased: Object.fromEntries(ACTIONS.map(action => [action, true])),
    axes: [0, 0, 0, 0],
    axisX: 0,
    axisY: 0,
    buttons: [],
    connected: false,
    down: { ...previousActions },
    dpad: { ...previousDpad, ...edgeState(previousDpad) },
    edges: {
      actions: actionEdges(previousActions),
      dpad: edgeState(previousDpad),
      stick: edgeState(previousStick),
    },
    focused: null,
    gamepad: { axes: [0, 0, 0, 0], buttons: [], connected: false, id: '', index: null },
    id: '',
    index: null,
    keys: {},
    look: { x: 0, y: 0 },
    pointer: { x: 0, y: 0, deltaX: 0, deltaY: 0, active: false, captured: false, dragging: false, id: null, type: '' },
    pressed: { ...previousActions },
    stick: { ...previousStick, ...edgeState(previousStick) },
    virtual: false,
    virtualGamepad: null,
  };

  const focusOwner = () => root?.activeElement ?? root?.ownerDocument?.activeElement ?? null;

  const insideRoot = element => {
    if (!element) return false;
    if (typeof root?.contains === 'function') return root.contains(element);
    return true;
  };

  const hiddenByAncestor = element => {
    for (let node = element; node; node = node.parentElement) {
      if (node.hidden || node.getAttribute?.('aria-hidden') === 'true' || node.hasAttribute?.('inert')) return true;
      const styleView = view?.getComputedStyle ? view : node.ownerDocument?.defaultView;
      if (styleView?.getComputedStyle && (node !== root || root?.nodeType === 1)) {
        try {
          const style = styleView.getComputedStyle(node);
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.contentVisibility === 'hidden') return true;
        } catch {
          return true;
        }
      }
      if (node === root) break;
    }
    return false;
  };

  const focusables = () => {
    if (typeof root?.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(element => {
      if (element.getAttribute?.('tabindex') === '-1') return false;
      if (hiddenByAncestor(element)) return false;
      if (typeof element.getClientRects === 'function' && element.getClientRects().length === 0) return false;
      return insideRoot(element);
    });
  };

  const focusCurrent = () => {
    const focused = focusOwner();
    return focused && focusables().includes(focused) ? focused : null;
  };

  const describeFocus = element => element?.id || element?.getAttribute?.('name') || null;

  const elementBox = element => {
    if (typeof element?.getBoundingClientRect !== 'function') return null;
    const box = element.getBoundingClientRect();
    return {
      bottom: box.bottom,
      height: box.height,
      left: box.left,
      right: box.right,
      top: box.top,
      width: box.width,
    };
  };

  const moveFocus = direction => {
    const elements = focusables();
    if (!elements.length) return null;
    const delta = typeof direction === 'string'
      ? ({ ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0], down: [0, 1], left: [-1, 0], right: [1, 0], up: [0, -1] }[direction] ?? [0, 0])
      : [number(direction?.x), number(direction?.y)];
    const current = focusCurrent();
    if (!current) {
      const first = delta[1] < 0 || delta[0] < 0 ? elements.at(-1) : elements[0];
      first?.focus?.();
      return first ?? null;
    }
    const currentIndex = elements.indexOf(current);
    const currentBox = elementBox(current);
    let target = null;
    let best = Infinity;
    elements.forEach((element, index) => {
      if (element === current) return;
      const box = elementBox(element);
      const dx = box && currentBox
        ? (box.left + box.width / 2) - (currentBox.left + currentBox.width / 2)
        : index - currentIndex;
      const dy = box && currentBox
        ? (box.top + box.height / 2) - (currentBox.top + currentBox.height / 2)
        : index - currentIndex;
      if ((delta[0] > 0 && dx <= 0) || (delta[0] < 0 && dx >= 0) || (delta[1] > 0 && dy <= 0) || (delta[1] < 0 && dy >= 0)) return;
      const primary = Math.abs(dx * delta[0]) + Math.abs(dy * delta[1]);
      const secondary = Math.abs(dx * delta[1]) + Math.abs(dy * delta[0]);
      const score = primary + secondary * 2;
      if (score < best) {
        best = score;
        target = element;
      }
    });
    if (!target && Math.abs(delta[1]) > 0) {
      const currentIndex = elements.indexOf(focusCurrent());
      const targetIndex = delta[1] > 0
        ? (currentIndex + 1) % elements.length
        : (currentIndex - 1 + elements.length) % elements.length;
      target = elements[targetIndex];
    }
    target?.focus?.();
    return target ?? null;
  };

  const activateFocused = () => {
    const focused = focusCurrent();
    if (!focused) return false;
    if (typeof focused.click === 'function') {
      focused.click();
      return true;
    }
    return false;
  };

  const getButtons = state => buttonList(state?.buttons);
  const getAxes = state => {
    const source = state?.axes;
    if (Array.isArray(source)) return [0, 1, 2, 3].map(index => number(source[index]));
    if (source && typeof source === 'object') {
      return [number(source.left), number(source.right), number(source.up), number(source.down)];
    }
    const left = state?.leftStick ?? state?.move ?? {};
    const right = state?.rightStick ?? state?.look ?? {};
    return [number(left.x), number(left.y), number(right.x), number(right.y)];
  };

  const poll = () => {
    let physical = null;
    if (typeof provider === 'function') {
      try {
        const pads = provider() ?? [];
        physical = Array.from(pads).find(pad => pad?.connected) ?? null;
      } catch {
        physical = null;
      }
    }
    const physicalButtons = getButtons(physical ?? {});
    const physicalAxes = getAxes(physical ?? {});
    const virtualActive = Boolean(virtualGamepad && virtualGamepad.connected !== false);
    const virtualButtons = getButtons(virtualGamepad);
    const virtualAxes = getAxes(virtualGamepad ?? {});
    const sourceVirtualActions = virtualActive
      ? (virtualGamepad?.actions ?? virtualGamepad ?? {})
      : {};
    const buttons = physical ? physicalButtons : virtualButtons;
    const axes = physical ? physicalAxes : virtualAxes;
    const source = physical
      ? `physical:${String(physical.id ?? '')}:${number(physical.index, -1)}`
      : virtualActive ? 'virtual' : '';
    if (source !== previousSource) buttonLatches.fill(false);

    const activeButtons = [];
    const padActions = Object.fromEntries(ACTIONS.map(action => [action, false]));
    const padPressed = Object.fromEntries(ACTIONS.map(action => [action, false]));
    const focused = focusCurrent();
    const buttonCount = Math.min(buttons.length, buttonLatches.length);
    for (let index = 0; index < buttonCount; index += 1) {
      const wasActive = buttonLatches[index];
      const isActive = buttonActive(buttons[index], wasActive);
      buttonLatches[index] = isActive;
      activeButtons[index] = isActive;
      if (!isActive) continue;
      const mapped = BUTTON_ACTION[index];
      const mappedActions = Array.isArray(mapped) ? mapped : mapped ? [mapped] : [];
      for (const action of mappedActions) padActions[action] = true;
      if (!wasActive && mappedActions.length) {
        const action = focused
          ? (mappedActions.includes('confirm') ? 'confirm' : mappedActions.includes('back') ? 'back' : mappedActions.includes('interact') ? 'interact' : mappedActions[0])
          : (mappedActions.find(value => value !== 'confirm' && value !== 'back' && value !== 'interact') ?? mappedActions[0]);
        padPressed[action] = true;
      }
    }

    const hasKey = (...codes) => codes.some(code => keys.has(code));
    const virtualDpad = !physical && virtualActive ? virtualGamepad?.dpad : null;
    const dpadNow = directionState(
      hasKey('ArrowUp', 'up') || Boolean(activeButtons[12]) || Boolean(virtualDpad?.up),
      hasKey('ArrowDown', 'down') || Boolean(activeButtons[13]) || Boolean(virtualDpad?.down),
      hasKey('ArrowLeft', 'left') || Boolean(activeButtons[14]) || Boolean(virtualDpad?.left),
      hasKey('ArrowRight', 'right') || Boolean(activeButtons[15]) || Boolean(virtualDpad?.right),
    );
    const leftX = deadzone(axes[0], deadzoneThreshold);
    const leftY = deadzone(-axes[1], deadzoneThreshold);
    const stickNow = directionState(leftY > stickThreshold, leftY < -stickThreshold, leftX < -stickThreshold, leftX > stickThreshold);
    const keyboardX = (hasKey('KeyD', 'd') ? 1 : 0) - (hasKey('KeyA', 'a') ? 1 : 0);
    const keyboardY = (hasKey('KeyW', 'w') ? 1 : 0) - (hasKey('KeyS', 's') ? 1 : 0);
    const touchMoveX = clamp(analog.moveX);
    const touchMoveY = clamp(analog.moveY);
    const move = normalizePair(clamp(keyboardX + (dpadNow.right ? 1 : 0) - (dpadNow.left ? 1 : 0) + leftX + touchMoveX), clamp(keyboardY + (dpadNow.up ? 1 : 0) - (dpadNow.down ? 1 : 0) + leftY + touchMoveY));
    const keyboardActions = Object.fromEntries(ACTIONS.map(action => [action, false]));
    for (const code in KEY_ACTION) {
      if (keys.has(code)) keyboardActions[KEY_ACTION[code]] = true;
    }
    if (hasKey('Shift', 'shift')) keyboardActions.sprint = true;
    if (hasKey('Control', 'control')) keyboardActions.crouch = true;
    const manualActions = Object.fromEntries(ACTIONS.map(action => [action, Boolean(manual[action].down)]));
    const virtualActions = Object.fromEntries(ACTIONS.map(action => [action, Boolean(sourceVirtualActions[action])]));
    const actions = Object.fromEntries(ACTIONS.map(action => [action, Boolean(manualActions[action] || keyboardActions[action] || virtualActions[action])]));
    for (const action of ACTIONS) if (padActions[action]) actions[action] = true;

    const pointerX = deadzone(pointer.deltaX / pointerScale, deadzoneThreshold);
    const pointerY = deadzone(-pointer.deltaY / pointerScale, deadzoneThreshold);
    const look = normalizePair(
      deadzone(axes[2], deadzoneThreshold) + pointerX + clamp(analog.lookX),
      deadzone(-axes[3], deadzoneThreshold) + pointerY + clamp(analog.lookY),
    );
    const pointerState = {
      x: pointerX,
      y: pointerY,
      deltaX: pointer.deltaX,
      deltaY: pointer.deltaY,
      active: pointer.active,
      captured: pointer.captured,
      dragging: pointer.dragging,
      id: pointer.id,
      type: pointer.type,
    };
    pointer.deltaX = 0;
    pointer.deltaY = 0;

    const dpadEdges = edgeState(dpadNow, previousDpad);
    const stickEdges = edgeState(stickNow, previousStick);
    const actionEdge = actionEdges(actions);
    for (const action of ACTIONS) {
      const wasActive = previousActions[action];
      const padFresh = padPressed[action] && !previousKeyboardActions[action] && !previousVirtualActions[action] && !previousManualActions[action];
      const padOnly = padActions[action] && !manualActions[action] && !keyboardActions[action] && !virtualActions[action];
      if (!actions[action]) {
        actionEdge.pressed[action] = false;
        actionEdge.released[action] = Boolean(wasActive);
      } else {
        actionEdge.pressed[action] = Boolean((!wasActive || padFresh) && (!padOnly || padPressed[action]) && !consumed[action]);
        if (padOnly && !padPressed[action]) actionEdge.pressed[action] = false;
        actionEdge.released[action] = false;
      }
      if (!actions[action] && !manual[action].down) consumed[action] = false;
    }
    previousDpad = dpadNow;
    previousStick = stickNow;
    previousActions = actions;
    previousKeyboardActions = keyboardActions;
    previousVirtualActions = virtualActions;
    previousManualActions = manualActions;
    previousSource = source;
    const virtualNormalized = virtualGamepad
      ? { axes: virtualAxes, buttons: virtualButtons, connected: virtualActive, id: 'virtual', index: -1 }
      : null;
    const gamepad = physical
      ? { axes, buttons, connected: true, id: String(physical.id ?? ''), index: number(physical.index, -1) }
      : { axes: [0, 0, 0, 0], buttons: [], connected: false, id: '', index: null };
    const keyState = {};
    for (const key of keys) keyState[key] = true;
    const analogState = { move: { x: analog.moveX, y: analog.moveY }, look: { x: analog.lookX, y: analog.lookY } };
    current = {
      analog: analogState,
      actions: { ...actions },
      actionPressed: { ...actionEdge.pressed },
      actionReleased: { ...actionEdge.released },
      axes: [...axes],
      axisX: move.x,
      axisY: move.y,
      buttons: [...buttons],
      connected: gamepad.connected,
      down: { ...actions },
      dpad: { ...dpadNow, ...dpadEdges },
      edges: { actions: actionEdge, dpad: dpadEdges, stick: stickEdges },
      focused: describeFocus(focused),
      gamepad,
      id: gamepad.id,
      index: gamepad.index,
      keys: keyState,
      look: { ...look },
      move: { ...move },
      pointer: pointerState,
      pressed: { ...actionEdge.pressed },
      stick: { ...stickNow, ...stickEdges },
      virtual: Boolean(virtualGamepad),
      virtualGamepad: virtualNormalized,
    };
    return current;
  };

  const press = action => {
    if (!ACTIONS.includes(action)) return false;
    if (!manual[action].down && !current.actions[action]) manual[action].pressed = true;
    manual[action].down = true;
    return true;
  };

  const release = action => {
    if (!ACTIONS.includes(action)) return false;
    manual[action].down = false;
    manual[action].pressed = false;
    return true;
  };

  const consume = action => {
    if (!ACTIONS.includes(action) || consumed[action]) return false;
    if (!manual[action].pressed && !current.actionPressed[action]) return false;
    consumed[action] = true;
    manual[action].pressed = false;
    current.actionPressed[action] = false;
    current.pressed[action] = false;
    current.edges.actions.pressed[action] = false;
    return true;
  };

  const suppress = action => {
    if (!ACTIONS.includes(action)) return false;
    consumed[action] = true;
    return true;
  };
  const setAnalogStick = (state) => {
    if (!state || typeof state !== 'object') {
      analog.moveX = 0;
      analog.moveY = 0;
      analog.lookX = 0;
      analog.lookY = 0;
      return controller;
    }
    const move = state.move || {};
    const look = state.look || {};
    analog.moveX = clamp(number(state.moveX ?? move.x));
    analog.moveY = clamp(number(state.moveY ?? move.y));
    analog.lookX = clamp(number(state.lookX ?? look.x));
    analog.lookY = clamp(number(state.lookY ?? look.y));
    return controller;
  };

  const setVirtualGamepad = state => {
    virtualGamepad = state && typeof state === 'object' ? state : null;
    return controller;
  };

  const clearPointer = () => {
    pointer.deltaX = 0;
    pointer.deltaY = 0;
    pointer.active = false;
    pointer.captured = false;
    pointer.dragging = false;
    pointer.id = null;
    pointer.type = '';
    current.pointer = { x: 0, y: 0, deltaX: 0, deltaY: 0, active: false, captured: false, dragging: false, id: null, type: '' };
  };

  const setPointer = state => {
    if (!state || typeof state !== 'object') {
      clearPointer();
      return controller;
    }
    const limit = pointerScale * 4;
    pointer.deltaX = Math.max(-limit, Math.min(limit, number(state.deltaX ?? state.movementX ?? state.x)));
    pointer.deltaY = Math.max(-limit, Math.min(limit, number(state.deltaY ?? state.movementY ?? state.y)));
    pointer.active = Boolean(state.active ?? state.down ?? true);
    pointer.captured = Boolean(state.captured);
    pointer.dragging = Boolean(state.dragging ?? pointer.active);
    pointer.id = state.id ?? state.pointerId ?? null;
    pointer.type = String(state.type ?? state.pointerType ?? '');
    return controller;
  };

  const tokenizeKey = event => {
    const tokens = [];
    if (event.code) tokens.push(event.code);
    if (event.key) tokens.push(event.key.length === 1 ? event.key.toLowerCase() : event.key);
    return tokens;
  };

  const isEditable = target => Boolean(
    target?.isContentEditable
    || ['INPUT', 'SELECT', 'TEXTAREA'].includes(target?.tagName)
    || target?.getAttribute?.('role') === 'textbox',
  );

  const onKeyDown = event => {
    const tokens = tokenizeKey(event);
    tokens.forEach(token => keys.add(token));
    const direction = ({ ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up' })[event.key];
    if (direction && !isEditable(event.target) && focusCurrent()) {
      event.preventDefault?.();
      moveFocus(direction);
    }
  };

  const onKeyUp = event => tokenizeKey(event).forEach(token => keys.delete(token));
  const clearInput = () => {
    keys.clear();
    clearPointer();
    // A thumb that is still down when the tab loses focus will never send its pointerup, so the
    // analogue channel has to be zeroed with the keys or the hunter walks into the dark forever.
    analog.moveX = 0;
    analog.moveY = 0;
    analog.lookX = 0;
    analog.lookY = 0;
  };

  if (typeof root?.addEventListener === 'function') {
    root.addEventListener('keydown', onKeyDown);
    root.addEventListener('keyup', onKeyUp);
    root.addEventListener('blur', clearInput);
  }
  if (root?.visibilityState === 'hidden') clearInput();
  else if (typeof root?.addEventListener === 'function') root.addEventListener('visibilitychange', clearInput);

  const controller = {
    get actions() { return current.actions; },
    get actionPressed() { return current.actionPressed; },
    get analog() { return current.analog; },
    get axisX() { return current.axisX; },
    get axisY() { return current.axisY; },
    get buttons() { return current.buttons; },
    get connected() { return current.connected; },
    focusCurrent,
    get down() { return current.down; },
    get edges() { return current.edges; },
    get dpad() { return current.dpad; },
    get gamepad() { return current.gamepad; },
    get id() { return current.id; },
    get index() { return current.index; },
    get keys() { return current.keys; },
    get look() { return current.look; },
    get move() { return current.move; },
    get pointer() { return current.pointer; },
    get pressed() { return current.pressed; },
    get stick() { return current.stick; },
    get virtual() { return Boolean(virtualGamepad); },
    get virtualGamepad() { return current.virtualGamepad; },
    setAnalogStick,
    activateFocused,
    back: () => consume('back'),
    consume,
    suppress,
    destroy: () => {
      if (typeof root?.removeEventListener === 'function') {
        root.removeEventListener('keydown', onKeyDown);
        root.removeEventListener('keyup', onKeyUp);
        root.removeEventListener('blur', clearInput);
        root.removeEventListener('visibilitychange', clearInput);
      }
      clearInput();
      if (options.exposeGlobal && view?.__LONEWOLF_CONTROLLER__ === controller) delete view.__LONEWOLF_CONTROLLER__;
    },
    focusables,
    moveFocus,
    press,
    release,
    setPointer,
    setVirtualGamepad,
    snapshot: poll,
  };

  poll();
  if (options.exposeGlobal && view) view.__LONEWOLF_CONTROLLER__ = controller;
  return controller;
}
